import * as firebase from '@/firebase';
import moment from 'moment';
import DateParser from '@/parsers/date';
import TeamsService from '@/services/teams';

const {
  usersCollection, doppelganger, auth: { currentUser },
} = firebase;

export default class UsersService {
  static async fetchUserProfile(userId) {
    const profile = await usersCollection.doc(userId).get();

    return profile.data();
  }

  static async fetchUsersByCompany(companyId) {
    const users = [];
    const snapshot = await usersCollection
      .where('company_id', '==', companyId)
      .orderBy('first_name')
      .get();

    snapshot.forEach((doc) => users.push({ uid: doc.id, ...doc.data() }));

    return users;
  }

  static async fetchOwnerById(ownerId) {
    const owner = await usersCollection.doc(ownerId).get();

    return owner.data();
  }

  static async fetchUserProfilesByCompany(companyId) {
    const users = await UsersService.fetchUsersByCompany(companyId);

    return Promise.all(
      users.map(async (user) => {
        const owner = await this.fetchOwnerById(user.user_id);

        return {
          ...user,
          name: `${user.first_name} ${user.last_name}`,
          info: `Criado por ${owner.first_name} ${owner.last_name} em ${DateParser.parseFromTimestamp(user.created_at.seconds)}`,
        };
      }),
    );
  }

  static async fetchCompanyBrokers({ companyId, teamSlug }) {
    const users = await UsersService.fetchUsersByCompany(companyId);
    let teamMembers = [];

    if (teamSlug) {
      const { uid } = await TeamsService.fetchTeamBySlug({ companyId, slug: teamSlug });
      const members = await TeamsService.fetchTeamMembers(uid);

      teamMembers = members.map((member) => member.user_id);
    }

    return users
      .filter((user) => user.type === 3)
      .filter((user) => (teamMembers.length > 0 ? teamMembers.includes(user.uid) : true));
  }

  static async createUserCredentials({ email, password }) {
    const user = await doppelganger.auth().createUserWithEmailAndPassword(email, password);
    doppelganger.auth().signOut();

    return user;
  }

  static async createUserProfile(uid, data) {
    const seconds = moment().unix();

    return usersCollection.doc(uid).set({
      user_id: data.userId,
      company_id: data.companyId,
      email: data.email,
      first_name: data.firstName,
      last_name: data.lastName,
      type: data.type,
      is_active: true,
      created_at: { seconds },
      updated_at: { seconds },
    });
  }

  static async updateUserProfile(data) {
    const seconds = moment().unix();

    return usersCollection.doc(data.userId).set({
      email: data.email,
      first_name: data.firstName,
      last_name: data.lastName,
      type: data.type,
      updated_at: { seconds },
    }, { merge: true });
  }

  static async updateCurrentUserInfo(data) {
    const seconds = moment().unix();

    return usersCollection
      .doc(firebase.auth.currentUser.uid)
      .update({
        first_name: data.firstName,
        last_name: data.lastName,
        updated_at: { seconds },
      });
  }

  static async updateCurrentUserEmail(email) {
    return currentUser.updateEmail(email);
  }

  static async updateCurrentUserPassword(password) {
    return firebase.auth.currentUser.updatePassword(password);
  }

  static async deactivateUser(userId) {
    const seconds = moment().unix();

    return usersCollection
      .doc(userId)
      .update({
        is_active: false,
        updated_at: { seconds },
      });
  }
}
